/* Traceability Events list page — Phase 4
 *
 * Lists all saved Traceability Events for the tenant. Each row shows the
 * event reference, starting point, current status, affected customers /
 * batches counts, created + last-updated timestamps, and quick actions
 * (View, Export PDF).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { listTraceEvents, downloadTraceEventPdf } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Loader2, FileText, ShieldAlert, Eye, Download, RefreshCw, Search } from 'lucide-react';

const EVENT_STATUSES = [
  { value: 'all',            label: 'All statuses',           color: 'bg-zinc-100 text-zinc-700' },
  { value: 'draft',          label: 'Draft',                  color: 'bg-zinc-200 text-zinc-700' },
  { value: 'under_review',   label: 'Under review',           color: 'bg-amber-100 text-amber-800' },
  { value: 'approved',       label: 'Approved internally',    color: 'bg-blue-100 text-blue-800' },
  { value: 'comms_prepared', label: 'Customer comms prepared',color: 'bg-purple-100 text-purple-800' },
  { value: 'closed',         label: 'Closed',                 color: 'bg-emerald-100 text-emerald-800' },
];

function statusPill(value) {
  const s = EVENT_STATUSES.find(x => x.value === value) || EVENT_STATUSES[1];
  return <Badge className={`${s.color} border-0 font-normal`}>{s.label}</Badge>;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function TraceEventsList() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [downloading, setDownloading] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = status === 'all' ? {} : { status };
      const r = await listTraceEvents(params);
      setEvents(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load events');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return events;
    return events.filter(e =>
      (e.event_ref || '').toLowerCase().includes(q) ||
      (e.starting || '').toLowerCase().includes(q) ||
      (e.starting_value || '').toLowerCase().includes(q),
    );
  }, [events, search]);

  const handleExport = async (e) => {
    e.stopPropagation?.();
    const ev = events.find(x => x.id === e.currentTarget?.dataset?.id);
    const eventId = e.currentTarget?.dataset?.id;
    if (!eventId) return;
    setDownloading(eventId);
    try {
      const r = await downloadTraceEventPdf(eventId);
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ref = (ev && ev.event_ref) || eventId;
      a.download = `${ref}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'PDF export failed');
    } finally { setDownloading(null); }
  };

  return (
    <div className="space-y-4" data-testid="trace-events-list-page">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <ShieldAlert className="w-5 h-5 text-amber-600" />
          <h1 className="text-xl font-semibold">Traceability Events</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Every saved Traceability / Mock Recall event for your site. Re-open
          past events to review evidence, continue an investigation, or export
          the full response pack as a PDF for FSAI / BRCGS / retailer audits.
        </p>
      </div>

      <Card>
        <CardContent className="pt-4">
          <div className="grid md:grid-cols-4 gap-3 items-end">
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EVENT_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-wider text-zinc-500">Search</label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 w-4 h-4 text-zinc-400" />
                <Input
                  value={search}
                  onChange={(ev) => setSearch(ev.target.value)}
                  placeholder="Event ref, starting point or lot number…"
                  className="pl-8"
                  data-testid="filter-search"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={load} data-testid="btn-refresh">
                <RefreshCw className="w-4 h-4 mr-1" />Refresh
              </Button>
              <Link to="/traceability/recall">
                <Button data-testid="btn-new-event"><FileText className="w-4 h-4 mr-1" />New event</Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />Loading events…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-muted-foreground py-16">
              <FileText className="w-8 h-8 mx-auto mb-3 opacity-40" />
              <div className="font-medium mb-1">No traceability events yet</div>
              <div className="text-sm">Run a <Link to="/traceability/recall" className="text-blue-700 underline">Mock Recall</Link> and save it to create your first event.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="events-table">
                <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="text-left px-3 py-2">Event ref</th>
                    <th className="text-left px-3 py-2">Trigger</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-right px-3 py-2">Customers</th>
                    <th className="text-right px-3 py-2">Batches</th>
                    <th className="text-left px-3 py-2">Created</th>
                    <th className="text-left px-3 py-2">Updated</th>
                    <th className="text-right px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(ev => {
                    const s = ev.summary_snapshot || {};
                    const custCount = (ev.affected_customers && ev.affected_customers.length)
                      || s.customers_count || 0;
                    const batchCount = s.cheese_batches_count || 0;
                    return (
                      <tr key={ev.id} className="border-t hover:bg-zinc-50/60" data-testid={`event-row-${ev.event_ref || ev.id}`}>
                        <td className="px-3 py-2 font-mono text-xs">
                          <Link to={`/traceability/events/${ev.id}`} className="text-blue-700 hover:underline">
                            {ev.event_ref || ev.id.slice(0, 8)}
                          </Link>
                        </td>
                        <td className="px-3 py-2">
                          <div className="truncate max-w-[280px]" title={ev.starting}>{ev.starting || '—'}</div>
                          {ev.starting_value && <div className="text-[11px] text-muted-foreground font-mono">{ev.starting_value}</div>}
                        </td>
                        <td className="px-3 py-2">{statusPill(ev.status)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{custCount}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{batchCount}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{fmtDate(ev.created_at)}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{fmtDate(ev.updated_at || ev.created_at)}</td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex gap-1 justify-end">
                            <Link to={`/traceability/events/${ev.id}`}>
                              <Button size="sm" variant="ghost" data-testid={`btn-view-${ev.event_ref || ev.id}`}>
                                <Eye className="w-4 h-4" />
                              </Button>
                            </Link>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={handleExport}
                              data-id={ev.id}
                              disabled={downloading === ev.id}
                              data-testid={`btn-pdf-${ev.event_ref || ev.id}`}
                            >
                              {downloading === ev.id
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <Download className="w-4 h-4" />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
